/**
 * Trade Loop Landing Page
 *
 * Main marketing landing page shown to visitors at the root URL.
 *
 * The copy, section order and inline illustrations came from a client-supplied mockup
 * ("V1 Final Landing Page Trade Loop.tsx"). That mockup was treated as a CONTENT source
 * only — its layout regressed on large screens, so the sizing here deliberately departs
 * from it, and those departures were confirmed over a round of review:
 *
 *  1. Header — the mockup pinned the logo at h-12 and capped the nav at max-w-7xl, so above
 *     1280px the logo and CTAs sat in a narrow band mid-screen. The bar now uses its own
 *     uncapped shell (HEADER_SHELL) and spans the viewport at any width. It is deliberately
 *     a fixed, compact h-16 with a fixed-size logo and text-base nav: an earlier pass scaled
 *     the header up with the viewport (logo to h-16, bar to h-24) and that was rejected as
 *     too tall and too heavy. Do not reintroduce the growth steps here.
 *  2. Header is fully opaque (`bg-white`, no backdrop-blur) with a permanent bottom border.
 *     The mockup and the previous landing page both used a translucent blurred bar that only
 *     grew a border once scrolled; review found the nav text unreadable over solid-coloured
 *     sections, and the bar indistinguishable from the white hero at rest. The scrolled state
 *     now only adds the shadow.
 *  3. Type scale — the mockup capped headings at `sm:`/`lg:` (h1 topped out at 2.75rem). Every
 *     heading and lead paragraph carries xl:/2xl: steps, matching the growth the previous
 *     landing page had (it ran to lg:text-7xl). Unlike the header, the BODY is meant to grow.
 *
 * Also kept from the previous landing page rather than the mockup, because the mockup has no
 * equivalent and these are load-bearing in the shipped app:
 *  - `env(safe-area-inset-top)` in the fixed header's and the hero's top padding
 *    (Capacitor WebView notch handling).
 *  - The authenticated-user redirect and loading state.
 *  - Live Stripe pricing via usePricing() instead of the mockup's hardcoded prices.
 *  - FREE_TRIAL_ENABLED-aware Pro CTA.
 *
 * The mockup's `trackEvent` calls were dropped: there is no analytics module in this app
 * (`@/lib/analytics` does not exist).
 */

import { useState, useEffect, useRef, type TouchEvent as ReactTouchEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePricing } from '@/hooks/usePricing';
import { FREE_TRIAL_ENABLED } from '@/config/subscription-plan-limits';
import AppStoreBadges, { APP_STORE_BADGES_VISIBLE } from '@/components/AppStoreBadges';
import {
  Menu, X, ChevronDown, ArrowRight, Shield, Eye, TrendingUp,
  CheckCircle, Check, AlertCircle, MapPin, Users, Zap,
  Network, Wrench, QrCode,
  ChevronLeft, ChevronRight, Pause, Play, Minus,
} from 'lucide-react';

// ─── Shared responsive primitives ─────────────────────────────────────────────
// Every section pulls its container/spacing/type from these so the page scales as
// one system. Adding an xl:/2xl: step here lifts the whole page at once.

/** Content shell — readable cap that still grows past the mockup's hard 1280px. */
const SHELL = 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 xl:px-12 2xl:max-w-[96rem] 2xl:px-16';
/** Header shell — intentionally uncapped so the bar's contents span the screen. */
const HEADER_SHELL = 'mx-auto w-full px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-20';
const SECTION_Y = 'py-16 sm:py-20 lg:py-24 xl:py-28';
const H2 = 'text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 tracking-tight';
const H3_FEATURE = 'text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 tracking-tight';
const LEAD = 'text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed';
const BODY = 'text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed';
/** Illustration cards grow with the viewport instead of freezing at 380px. */
const CARD_MAX = 'w-full max-w-[380px] xl:max-w-[460px] 2xl:max-w-[520px] mx-auto';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PricingPlan {
  name: string;
  price: string;
  period?: string;
  description: string;
  features: string[];
  cta: string;
  href: string;
  highlighted: boolean;
  available: boolean;
  badge?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface Testimonial {
  quote: string;
  name: string;
  company: string;
  location: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const CONTRACTOR_TESTIMONIALS: Testimonial[] = [
  {
    quote: "Trade Loop helps us keep our trusted partners organized and quickly connect customers with the right contractor. When a customer has an active leak, we can request availability from our plumbing partners and see who can respond fastest—without making a bunch of calls.",
    name: 'Luke G.',
    company: 'Kiwi Restoration',
    location: 'Boise, ID',
  },
  {
    quote: 'We get customers asking for trades outside of electrical all the time. Trade Loop gives us one place to keep the contractors we trust and make a quick introduction instead of searching through old texts and contacts.',
    name: 'Josh S.',
    company: 'Mythic Power Electric',
    location: 'Caldwell, ID',
  },
  {
    quote: 'Before Trade Loop, referrals could easily get buried in a text thread. Now we can send the customer information to a trusted partner, keep track of the referral, and know whether someone followed up.',
    name: 'Jason W.',
    company: 'HVAC Sales',
    location: 'Boise, ID',
  },
  {
    quote: 'In construction, you are constantly connecting customers with different trades. Trade Loop helps us keep our preferred contractors organized so we can make a confident handoff when a customer needs someone.',
    name: 'Jody B.',
    company: 'JB Construction',
    location: 'Middleton, ID',
  },
  {
    quote: 'There are times when a customer is outside our service area or needs help sooner than we can get there. Trade Loop makes it easy to send the opportunity to someone we trust and see what happens after the referral.',
    name: 'Justyn S.',
    company: 'Valley Wide Electric',
    location: 'Emmett, ID',
  },
  {
    quote: 'When we are booked out or a customer needs another trade, we still want to point them in the right direction. Trade Loop helps us stay organized and connect them with contractors we already know and trust.',
    name: 'Daniel C.',
    company: 'Kingdom Heating and Air',
    location: 'Fruitland, ID',
  },
  {
    quote: 'Our customers often ask about projects that fall outside the services we provide. Trade Loop gives us a simple way to connect them with a trusted local professional instead of just telling them we cannot help.',
    name: 'Lisa J.',
    company: 'Mr. Handyman of Boise, Meridian and Nampa',
    location: '',
  },
];

const TOTAL_TESTIMONIALS = CONTRACTOR_TESTIMONIALS.length;

const NAV_ANCHORS = [
  { label: 'How It Works', id: 'how-it-works' },
  { label: 'Features', id: 'features' },
  { label: 'Pricing', id: 'pricing' },
  { label: 'FAQ', id: 'faq' },
];

const BENEFITS = [
  {
    icon: <Shield className="w-5 h-5 xl:w-6 xl:h-6" />,
    title: 'Protect the Customer Relationship',
    body: 'Refer customers through a professional process instead of sending an unexplained phone number in a text.',
  },
  {
    icon: <Eye className="w-5 h-5 xl:w-6 xl:h-6" />,
    title: 'Know What Happened',
    body: 'See whether a referral is pending, accepted, declined, or completed.',
  },
  {
    icon: <Zap className="w-5 h-5 xl:w-6 xl:h-6" />,
    title: 'Respond Faster',
    body: 'Request availability before assigning the job and reduce the time your customer spends waiting.',
  },
  {
    icon: <Network className="w-5 h-5 xl:w-6 xl:h-6" />,
    title: 'Strengthen Your Partner Network',
    body: 'Keep dependable contractors organized by trade, company, and service area.',
  },
  {
    icon: <TrendingUp className="w-5 h-5 xl:w-6 xl:h-6" />,
    title: 'Understand the Business Impact',
    body: 'See the referral activity and revenue generated through your contractor relationships.',
  },
];

const HOW_IT_WORKS = [
  {
    number: '01',
    title: 'Choose a Trusted Partner',
    body: 'Select a contractor from your network or invite someone you already work with.',
  },
  {
    number: '02',
    title: 'Send the Complete Referral',
    body: 'Add the customer, job details, urgency, attachments, and any important notes.',
  },
  {
    number: '03',
    title: 'Track the Handoff',
    body: 'The receiving contractor responds, updates the status, and closes out the referral while you stay informed.',
  },
];

/**
 * Prices come from Stripe at runtime (usePricing), so the plan list is built per
 * render rather than declared as a module constant like the mockup did.
 */
function buildPricingPlans(proPrice: string, growPrice: string): PricingPlan[] {
  return [
    {
      name: 'Starter',
      price: 'Free',
      description: 'For contractors who want a professional referral system.',
      features: [
        'Unlimited partner connections',
        'Unlimited referrals',
        'Basic company profile',
        'Referral messaging',
        'QR partner connections',
        'Basic referral activity',
      ],
      cta: 'Create Free Account',
      href: '/login?mode=signup',
      highlighted: true,
      available: true,
      badge: 'Get Started',
    },
    {
      name: 'Pro',
      price: proPrice,
      period: '/month',
      description: 'For contractors who want a stronger professional presence and deeper referral insights.',
      features: [
        'Everything in Starter',
        'Professional company profile',
        'Advanced referral analytics',
        'Advanced revenue insights',
        'Enhanced partner-network tools',
        'Availability requests',
      ],
      cta: FREE_TRIAL_ENABLED ? 'Start Free Trial' : 'Get Started',
      href: FREE_TRIAL_ENABLED ? '/login?mode=signup&trial=true' : '/login?mode=signup',
      highlighted: false,
      available: true,
    },
    {
      name: 'Grow',
      price: growPrice,
      period: '/month',
      description: 'For companies that need team referral workflows and company-level management.',
      features: [
        'Everything in Pro',
        'Team referral submissions',
        'Central referral oversight',
        'Company-level reporting',
        'Multiple team members',
        'Admin dashboard',
      ],
      cta: 'Coming Later',
      href: '#pricing',
      highlighted: false,
      available: false,
      badge: 'Coming Later',
    },
  ];
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'Is Trade Loop a lead-generation company?',
    answer:
      'No. Trade Loop does not sell the same customer opportunity to multiple contractors. It helps contractors send and manage direct referrals through partners they choose.',
  },
  {
    question: 'Does Trade Loop replace my CRM?',
    answer:
      "No. Your CRM manages your company's customers, estimates, jobs, and invoices. Trade Loop manages the referral handoff and communication between separate contractor businesses.",
  },
  {
    question: 'I already have referral partners. Why would I need Trade Loop?',
    answer:
      'That is exactly where Trade Loop begins. Add the contractors you already trust, keep them organized, and stop relying on disconnected text threads to know what happened to each referral.',
  },
  {
    question: 'Do my partners have to be using Trade Loop already?',
    answer:
      'You can invite existing contractors through a link or QR code and begin building your network around the relationships you already have.',
  },
  {
    question: 'Who decides which contractor receives my referral?',
    answer:
      'You do. Trade Loop helps you organize and communicate with partners. It does not automatically distribute your referral to multiple contractors.',
  },
  {
    question: "Does Trade Loop guarantee another contractor's work?",
    answer:
      'No. Contractors remain responsible for selecting and qualifying their referral partners. Trade Loop provides the system for managing the referral and customer handoff.',
  },
  {
    question: 'How much does Trade Loop cost?',
    answer:
      'A free Trade Loop account includes unlimited referrals and partner connections. Paid plans add professional and advanced business tools.',
  },
  {
    question: 'Where is Trade Loop available?',
    answer:
      'Trade Loop is launching first with contractors in Boise and the Treasure Valley, with expansion planned for additional markets.',
  },
];

// ─── Utility ──────────────────────────────────────────────────────────────────

const HEADER_ID = 'tl-site-header';

/**
 * The mockup hardcoded a 68px header offset. This header's height is fluid
 * (and gains a safe-area inset on native), so measure it instead.
 */
function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const headerH = document.getElementById(HEADER_ID)?.offsetHeight ?? 0;
  const top = el.getBoundingClientRect().top + window.scrollY - headerH - 8;
  window.scrollTo({ top, behavior: 'smooth' });
}

// ─── Illustrations ────────────────────────────────────────────────────────────

function HeroMockup() {
  return (
    <div className="relative flex justify-center lg:justify-end" aria-hidden="true">
      <div className="relative w-[300px] sm:w-[340px] lg:w-[360px] xl:w-[420px] 2xl:w-[460px]">
        {/* Main referral list card */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-700 to-blue-600 px-4 py-3 xl:px-5 xl:py-4 flex items-center justify-between">
            <span className="text-sm xl:text-base font-bold text-white">My Referrals</span>
            <span className="text-xs xl:text-sm text-blue-200">3 active</span>
          </div>
          <div className="p-3 xl:p-4 space-y-2 xl:space-y-3 bg-gray-50">
            <div className="bg-white rounded-xl p-3 xl:p-4 shadow-sm border border-gray-100">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div>
                  <p className="text-xs xl:text-sm font-semibold text-gray-900">Panel Upgrade – 200A</p>
                  <p className="text-[10px] xl:text-xs text-gray-500 mt-0.5">→ Southwest Electric</p>
                </div>
                <span className="shrink-0 text-[10px] xl:text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                  Accepted
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] xl:text-xs text-gray-400">Customer: J. Martinez</span>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                  <span className="text-[10px] xl:text-xs text-green-600 font-medium">Active</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 xl:p-4 shadow-sm border border-gray-100">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div>
                  <p className="text-xs xl:text-sm font-semibold text-gray-900">HVAC Replacement</p>
                  <p className="text-[10px] xl:text-xs text-gray-500 mt-0.5">→ Valley HVAC Co.</p>
                </div>
                <span className="shrink-0 text-[10px] xl:text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  Pending
                </span>
              </div>
              <span className="text-[10px] xl:text-xs text-amber-600 font-medium">Awaiting response</span>
            </div>
            <div className="bg-white rounded-xl p-3 xl:p-4 shadow-sm border border-gray-100 opacity-70">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs xl:text-sm font-semibold text-gray-900">Roof Inspection</p>
                  <p className="text-[10px] xl:text-xs text-gray-500 mt-0.5">→ Eagle Roofing LLC</p>
                </div>
                <span className="shrink-0 text-[10px] xl:text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                  Completed
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Floating: availability badge — hidden on xs to prevent horizontal overflow */}
        <div className="hidden sm:block absolute -right-4 top-14 bg-white rounded-xl px-3 py-2 shadow-lg border border-gray-100">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="text-xs xl:text-sm font-semibold text-gray-800">Available</span>
          </div>
          <p className="text-[10px] xl:text-xs text-gray-400 mt-0.5">Valley HVAC replied</p>
        </div>

        {/* Floating: accepted notification — hidden on xs to prevent horizontal overflow */}
        <div className="hidden sm:block absolute -left-6 bottom-14 bg-white rounded-xl px-3 py-2.5 shadow-lg border border-gray-100 max-w-[148px] xl:max-w-[180px]">
          <p className="text-[10px] xl:text-xs font-semibold text-gray-800">Referral accepted</p>
          <p className="text-[10px] xl:text-xs text-gray-400 mt-0.5 leading-snug">Southwest Electric responded</p>
        </div>
      </div>
    </div>
  );
}

function AvailabilityIllustration() {
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-md p-4 xl:p-6 ${CARD_MAX}`} aria-hidden="true">
      <p className="text-[10px] xl:text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Availability Request</p>
      <div className="space-y-2 xl:space-y-3 mb-4">
        {[
          { name: 'Southwest Electric', status: 'Available', color: 'green' },
          { name: 'Valley Power Co.', status: 'Checking…', color: 'amber' },
          { name: 'Peak Electric LLC', status: 'Unavailable', color: 'gray' },
        ].map(({ name, status, color }) => (
          <div key={name} className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 xl:px-4 xl:py-3">
            <span className="text-sm xl:text-base font-medium text-gray-800">{name}</span>
            <span
              className={`shrink-0 text-xs xl:text-sm font-semibold px-2 py-0.5 rounded-full ${
                color === 'green'
                  ? 'bg-green-50 text-green-700'
                  : color === 'amber'
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {status}
            </span>
          </div>
        ))}
      </div>
      <div className="w-full py-2 xl:py-2.5 rounded-xl bg-blue-600 text-center text-xs xl:text-sm font-semibold text-white">
        Send Referral to Southwest Electric
      </div>
    </div>
  );
}

function StatusTrackingIllustration() {
  const steps = [
    { label: 'Referral Sent', done: true },
    { label: 'Partner Accepted', done: true },
    { label: 'Customer Contacted', done: true },
    { label: 'Job Completed', done: false },
  ];
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-md p-5 xl:p-7 ${CARD_MAX}`} aria-hidden="true">
      <div className="flex items-center justify-between gap-2 mb-4">
        <p className="text-sm xl:text-base font-semibold text-gray-900">Panel Upgrade – 200A</p>
        <span className="shrink-0 text-xs xl:text-sm font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">In Progress</span>
      </div>
      <div className="space-y-3 xl:space-y-4">
        {steps.map((step) => (
          <div key={step.label} className="flex items-center gap-3">
            <div
              className={`w-5 h-5 xl:w-6 xl:h-6 rounded-full flex items-center justify-center shrink-0 ${
                step.done ? 'bg-blue-600' : 'bg-gray-100 border-2 border-gray-200'
              }`}
            >
              {step.done && <Check className="w-3 h-3 xl:w-3.5 xl:h-3.5 text-white" />}
            </div>
            <span className={`text-sm xl:text-base ${step.done ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MessagingIllustration() {
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden ${CARD_MAX}`} aria-hidden="true">
      <div className="bg-gray-50 border-b border-gray-100 px-4 py-2.5 xl:px-5 xl:py-3">
        <p className="text-xs xl:text-sm font-semibold text-gray-700">Referral Thread – HVAC Replacement</p>
      </div>
      <div className="p-4 xl:p-5 space-y-3 xl:space-y-4">
        <div className="flex gap-2">
          <div className="w-6 h-6 xl:w-7 xl:h-7 rounded-full bg-blue-600 shrink-0 mt-0.5"></div>
          <div className="bg-blue-50 rounded-xl rounded-tl-sm px-3 py-2 xl:px-4 xl:py-2.5 max-w-[200px] xl:max-w-[260px]">
            <p className="text-xs xl:text-sm text-blue-900 leading-relaxed">Older Trane unit, needs full replacement. Moderate urgency. Does she have the model number?</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <div className="bg-gray-100 rounded-xl rounded-tr-sm px-3 py-2 xl:px-4 xl:py-2.5 max-w-[200px] xl:max-w-[260px]">
            <p className="text-xs xl:text-sm text-gray-700 leading-relaxed">Got it—I can be there Thursday. I'll bring a few options.</p>
          </div>
          <div className="w-6 h-6 xl:w-7 xl:h-7 rounded-full bg-gray-300 shrink-0 mt-0.5"></div>
        </div>
        <div className="flex gap-2">
          <div className="w-6 h-6 xl:w-7 xl:h-7 rounded-full bg-blue-600 shrink-0 mt-0.5"></div>
          <div className="bg-blue-50 rounded-xl rounded-tl-sm px-3 py-2 xl:px-4 xl:py-2.5 max-w-[200px] xl:max-w-[260px]">
            <p className="text-xs xl:text-sm text-blue-900 leading-relaxed">She said it's a 2008 XL20i. She's flexible on time.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function NetworkGapsIllustration() {
  const trades = [
    { name: 'Electrician', covered: true },
    { name: 'HVAC', covered: true },
    { name: 'Plumber', covered: true },
    { name: 'Roofer', covered: false },
    { name: 'Restoration', covered: false },
    { name: 'Remodeler', covered: false },
  ];
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-md p-4 xl:p-6 ${CARD_MAX}`} aria-hidden="true">
      <p className="text-[10px] xl:text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Partner Network</p>
      <div className="grid grid-cols-2 gap-2 xl:gap-3 mb-3">
        {trades.map(({ name, covered }) => (
          <div
            key={name}
            className={`flex items-center gap-2 rounded-xl px-3 py-2 xl:px-4 xl:py-2.5 text-xs xl:text-sm font-medium ${
              covered ? 'bg-blue-50 text-blue-800' : 'bg-red-50 text-red-600 border border-red-100'
            }`}
          >
            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${covered ? 'bg-blue-500' : 'bg-red-400'}`}></div>
            {name}
          </div>
        ))}
      </div>
      <p className="text-[10px] xl:text-xs text-red-500 font-semibold">3 trades with no coverage — focus here</p>
    </div>
  );
}

function QRConnectionIllustration() {
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-md p-5 xl:p-7 ${CARD_MAX}`} aria-hidden="true">
      <div className="flex flex-col items-center gap-4">
        <div className="w-28 h-28 xl:w-36 xl:h-36 bg-gray-900 rounded-xl flex items-center justify-center">
          <QrCode className="w-16 h-16 xl:w-20 xl:h-20 text-white" />
        </div>
        <div className="text-center">
          <p className="text-sm xl:text-base font-semibold text-gray-800">Mike's Plumbing</p>
          <p className="text-xs xl:text-sm text-gray-400 mt-0.5">Scan to connect on Trade Loop</p>
        </div>
        <div className="flex gap-2 w-full">
          <div className="flex-1 py-2 xl:py-2.5 rounded-xl bg-blue-600 text-center text-xs xl:text-sm font-semibold text-white">Share Link</div>
          <div className="flex-1 py-2 xl:py-2.5 rounded-xl border border-gray-200 text-center text-xs xl:text-sm font-semibold text-gray-600">Save QR</div>
        </div>
      </div>
    </div>
  );
}

function AnalyticsIllustration() {
  const bars = [45, 70, 55, 90, 65, 80, 60];
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-md p-4 xl:p-6 ${CARD_MAX}`} aria-hidden="true">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm xl:text-base font-semibold text-gray-800">Referral Activity</p>
        <span className="text-[10px] xl:text-xs text-gray-400">Last 7 days</span>
      </div>
      <div className="flex items-end gap-1.5 xl:gap-2 h-16 xl:h-24 mb-4">
        {/* `h-full` on each track matters: it is a flex item under `items-end`, so without
            an explicit height it collapses to 0 and the percentage-height fill inside
            renders nothing — the chart was blank in the mockup for this reason. */}
        {bars.map((h, i) => (
          <div key={i} className="flex-1 h-full rounded-t bg-blue-100 relative overflow-hidden">
            <div
              className="absolute bottom-0 left-0 right-0 bg-blue-500 rounded-t"
              style={{ height: `${h}%` }}
            ></div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100">
        {[
          { label: 'Sent', value: '12' },
          { label: 'Received', value: '8' },
          { label: 'Completed', value: '17' },
        ].map(({ label, value }) => (
          <div key={label} className="text-center">
            <p className="text-lg xl:text-2xl font-bold text-blue-700">{value}</p>
            <p className="text-[10px] xl:text-xs text-gray-400">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Section: Header ──────────────────────────────────────────────────────────

function MarketingHeader({ onNav }: { onNav: (id: string) => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setMenuOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <header
      id={HEADER_ID}
      /* `pt-[env(safe-area-inset-top)]` is carried over from the current landing
         page — without it the bar slides under the notch in the Capacitor WebView.
         The mockup had no safe-area handling. */
      className={`fixed inset-x-0 top-0 z-50 bg-white border-b border-gray-200 pt-[env(safe-area-inset-top)] transition-shadow duration-200 ${
        scrolled ? 'shadow-sm' : ''
      }`}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg text-sm font-semibold"
      >
        Skip to main content
      </a>

      <nav
        className={`${HEADER_SHELL} h-16 flex items-center justify-between gap-4`}
        aria-label="Main navigation"
      >
        <Link to="/" aria-label="Trade Loop — Home" className="shrink-0">
          <img src="/logo-with-text.png" alt="Trade Loop" className="h-9 sm:h-10 w-auto" />
        </Link>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-1">
          {NAV_ANCHORS.map(({ label, id }) => (
            <button
              key={id}
              onClick={() => onNav(id)}
              className="px-3 py-1.5 text-base font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {label}
            </button>
          ))}
        </div>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/login"
            className="px-3 py-1.5 text-base font-medium text-gray-600 hover:text-gray-900 transition-colors rounded-lg"
          >
            Sign In
          </Link>
          <Link
            to="/login?mode=signup"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            Create Free Account
          </Link>
        </div>

        {/* Mobile controls */}
        <div className="flex md:hidden items-center gap-1">
          <Link to="/login" className="px-3 py-1.5 text-base font-medium text-gray-600">
            Sign In
          </Link>
          <button
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            onClick={() => setMenuOpen((v) => !v)}
            className="p-2 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          id="mobile-nav"
          className="md:hidden border-t border-gray-100 bg-white px-4 pt-2 pb-4 space-y-1"
        >
          {NAV_ANCHORS.map(({ label, id }) => (
            <button
              key={id}
              onClick={() => { setMenuOpen(false); onNav(id); }}
              className="w-full text-left px-3 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
            >
              {label}
            </button>
          ))}
          <div className="pt-2 border-t border-gray-100">
            <Link
              to="/login?mode=signup"
              onClick={() => setMenuOpen(false)}
              className="block w-full text-center py-3 bg-blue-600 text-white text-base font-semibold rounded-xl mt-2 hover:bg-blue-700 transition-colors"
            >
              Create Free Account
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

// ─── Section: Hero ────────────────────────────────────────────────────────────

function HeroSection({ onHowItWorks }: { onHowItWorks: () => void }) {
  return (
    <section
      /* Clears the fixed 65px header plus the notch inset. Set here rather than via the old
         `pt-header-safe md:pt-header-normal` utility pair, whose md: half was never emitted
         by Tailwind (see index.css) — so the phone value applied on desktop too and the two
         breakpoints could not be tuned apart. Mobile is deliberately tight: 8rem left a 63px
         blank band under the header on a phone. */
      className="pt-[calc(5.5rem_+_env(safe-area-inset-top,0px))] md:pt-[calc(8rem_+_env(safe-area-inset-top,0px))] pb-16 sm:pb-20 lg:pb-24 xl:pb-28 relative overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse 90% 70% at 10% 60%, rgba(219,234,254,0.45) 0%, transparent 55%), ' +
          'radial-gradient(ellipse 60% 80% at 85% 20%, rgba(167,243,208,0.22) 0%, transparent 55%), ' +
          '#ffffff',
      }}
    >
      {/* Subtle network connection dots — decorative, aria-hidden */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute top-24 left-[8%] w-48 h-48 xl:w-72 xl:h-72 rounded-full bg-blue-100/30 blur-3xl" />
        <div className="absolute top-16 right-[12%] w-56 h-56 xl:w-80 xl:h-80 rounded-full bg-emerald-100/25 blur-3xl" />
        <div className="absolute bottom-8 left-[30%] w-32 h-32 xl:w-48 xl:h-48 rounded-full bg-blue-100/20 blur-2xl" />
      </div>

      <div className={`relative ${SHELL}`}>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 2xl:gap-20 items-center">
          {/* Copy */}
          <div className="text-center lg:text-left">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 mb-4 bg-blue-50 border border-blue-100 px-3 py-1.5 xl:px-4 xl:py-2 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" aria-hidden="true" />
              <span className="text-xs xl:text-sm font-bold text-blue-700 uppercase tracking-widest">
                Referral Management Built for Residential Contractors
              </span>
            </div>

            {/* Type scale runs well past the mockup's lg:text-[2.75rem] ceiling. The
                mockup's `whitespace-nowrap` on the gradient line is dropped — at these
                sizes it forces horizontal overflow on a phone. */}
            <h1 className="text-4xl sm:text-5xl xl:text-6xl 2xl:text-7xl font-extrabold text-gray-900 leading-[1.12] tracking-tight max-w-xl xl:max-w-2xl 2xl:max-w-3xl mx-auto lg:mx-0">
              Turn Contractor Referrals Into a System—
              <br className="hidden lg:block" />
              <span className="bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
                Not Another Text Thread
              </span>
            </h1>

            <p className={`mt-5 xl:mt-7 ${LEAD} max-w-xl xl:max-w-2xl 2xl:max-w-3xl mx-auto lg:mx-0`}>
              Trade Loop helps home-service contractors build a trusted network of local partners, send and track customer referrals, request availability, and keep every handoff organized from introduction through completion.
            </p>

            {/* Differentiation message — more prominent */}
            <div className="mt-4 xl:mt-6 flex items-start gap-2.5 max-w-lg xl:max-w-2xl mx-auto lg:mx-0 pl-3 border-l-2 border-emerald-400">
              <p className="text-sm lg:text-base xl:text-lg text-gray-500 leading-relaxed">
                Not a lead marketplace. Not a replacement for your CRM.{' '}
                <span className="font-medium text-gray-700">Your network, your customers, and your choice of referral partner.</span>
              </p>
            </div>

            <div className="mt-8 xl:mt-10 flex flex-col sm:flex-row gap-3 xl:gap-4 justify-center lg:justify-start">
              <Link
                to="/login?mode=signup"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 xl:px-8 xl:py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-600/20 text-white font-semibold rounded-xl transition-colors text-sm sm:text-base xl:text-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                Create Free Account
                <ArrowRight className="w-4 h-4 xl:w-5 xl:h-5" />
              </Link>
              <button
                onClick={onHowItWorks}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 xl:px-8 xl:py-4 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50/60 text-gray-700 font-semibold rounded-xl transition-colors text-sm sm:text-base xl:text-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                See How It Works
              </button>
            </div>
            <p className="mt-4 text-xs sm:text-sm xl:text-base text-gray-500 leading-relaxed">
              Free to start · Invite the contractors you already trust · Upgrade as your network grows
            </p>
          </div>

          {/* Visual */}
          <div className="mt-8 lg:mt-0 flex justify-center lg:justify-end">
            {/* Glow behind the mockup */}
            <div className="relative">
              <div
                className="absolute inset-0 -m-6 rounded-3xl blur-2xl opacity-40 pointer-events-none"
                aria-hidden="true"
                style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.25) 0%, rgba(5,150,105,0.2) 100%)' }}
              />
              <div className="relative">
                <HeroMockup />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Differentiation Strip ──────────────────────────────────────────

function DifferentiationStrip() {
  const points = [
    { icon: <Shield className="w-4 h-4 xl:w-5 xl:h-5" />, label: 'No shared leads' },
    { icon: <Users className="w-4 h-4 xl:w-5 xl:h-5" />, label: 'No bidding against other contractors' },
    { icon: <Network className="w-4 h-4 xl:w-5 xl:h-5" />, label: 'No required networking group' },
    { icon: <CheckCircle className="w-4 h-4 xl:w-5 xl:h-5" />, label: 'You choose where every referral goes' },
  ];
  return (
    <section
      className="border-y border-blue-100 py-5 xl:py-7"
      style={{
        background:
          'linear-gradient(90deg, rgba(239,246,255,0.85) 0%, rgba(236,253,245,0.75) 50%, rgba(239,246,255,0.85) 100%)',
      }}
    >
      <div className={SHELL}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
          {points.map(({ icon, label }) => (
            <div key={label} className="flex items-center gap-2.5 xl:gap-3">
              <div className="w-7 h-7 xl:w-9 xl:h-9 rounded-lg bg-white border border-blue-100 shadow-sm flex items-center justify-center text-blue-600 shrink-0">
                {icon}
              </div>
              <span className="text-xs sm:text-sm lg:text-base xl:text-lg font-semibold text-gray-800 leading-tight">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section: Problem ─────────────────────────────────────────────────────────

function ProblemSection() {
  return (
    <section id="for-contractors" className={`${SECTION_Y} bg-white scroll-mt-20`}>
      <div className={SHELL}>
        <div className="max-w-2xl xl:max-w-3xl mx-auto text-center mb-12 xl:mb-16">
          <h2 className={`${H2} mb-4`}>
            Valuable Referrals Should Not Disappear Into Text Threads
          </h2>
          <p className={LEAD}>
            A customer asks for work you do not provide. You text a contractor you trust, send the customer's information, and hope the handoff goes smoothly. Then the questions begin:
          </p>
          <ul className={`mt-5 xl:mt-7 space-y-2 ${BODY} text-left max-w-md xl:max-w-lg mx-auto`}>
            {[
              'Did the contractor see the referral?',
              'Did they accept it?',
              'Did they contact the customer?',
              'Was the work completed?',
              'Did the customer have a good experience?',
              'How much business are you sending back and forth?',
            ].map((q) => (
              <li key={q} className="flex items-start gap-2">
                <span className="text-gray-300 shrink-0 mt-0.5">—</span>
                {q}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm lg:text-base xl:text-lg text-gray-500 leading-relaxed">
            Trade Loop keeps the referral, communication, status, and history together so nothing depends on screenshots, scattered messages, or memory.
          </p>
        </div>

        {/* Before / After */}
        <div className="grid sm:grid-cols-2 gap-4 xl:gap-6 max-w-2xl xl:max-w-4xl mx-auto">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 xl:p-7">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 xl:w-8 xl:h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="w-3.5 h-3.5 xl:w-4 xl:h-4 text-gray-400" />
              </div>
              <span className="text-sm xl:text-base font-semibold text-gray-500">Without Trade Loop</span>
            </div>
            <ul className="space-y-3 xl:space-y-4">
              {['Text thread with customer info', 'Screenshot of address', 'No response confirmation', 'Unclear job status', 'No record of the relationship'].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm lg:text-base xl:text-lg text-gray-500">
                  <AlertCircle className="w-4 h-4 xl:w-5 xl:h-5 text-gray-300 shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5 xl:p-7">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 xl:w-8 xl:h-8 rounded-full bg-blue-600 flex items-center justify-center">
                <Check className="w-3.5 h-3.5 xl:w-4 xl:h-4 text-white" />
              </div>
              <span className="text-sm xl:text-base font-semibold text-blue-800">With Trade Loop</span>
            </div>
            <ul className="space-y-3 xl:space-y-4">
              {['Structured referral record', 'Customer info stays secure', 'Partner confirms receipt', 'Live status tracking', 'Complete referral history'].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm lg:text-base xl:text-lg text-blue-800">
                  <CheckCircle className="w-4 h-4 xl:w-5 xl:h-5 text-blue-600 shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section: How It Works ────────────────────────────────────────────────────

function HowItWorksSection() {
  return (
    <section id="how-it-works" className={`${SECTION_Y} bg-gray-50 scroll-mt-20`}>
      <div className={SHELL}>
        <div className="max-w-xl xl:max-w-3xl mx-auto text-center mb-12 xl:mb-16">
          <h2 className={H2}>
            From Customer Request to Completed Referral
          </h2>
          <p className={`mt-3 xl:mt-5 ${LEAD}`}>
            Trade Loop gives contractors a simple, professional workflow for handing a customer to someone they trust.
          </p>
        </div>
        <div className="relative grid md:grid-cols-3 gap-8 xl:gap-12 max-w-4xl xl:max-w-6xl mx-auto">
          <div className="hidden md:block absolute top-10 xl:top-12 left-[calc(16.67%+2.5rem)] right-[calc(16.67%+2.5rem)] h-px bg-blue-100" aria-hidden="true" />
          {HOW_IT_WORKS.map((step) => (
            <div key={step.number} className="text-center">
              <div className="w-20 h-20 xl:w-24 xl:h-24 rounded-2xl bg-blue-600 text-white flex items-center justify-center mx-auto mb-5 shadow-lg relative z-10">
                <span className="text-2xl xl:text-3xl font-extrabold">{step.number}</span>
              </div>
              <h3 className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mb-2">{step.title}</h3>
              <p className={BODY}>{step.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section: Benefits ────────────────────────────────────────────────────────

function BenefitsSection() {
  return (
    <section className={`${SECTION_Y} bg-white`}>
      <div className={SHELL}>
        <div className="max-w-xl xl:max-w-3xl mx-auto text-center mb-12 xl:mb-16">
          <h2 className={H2}>
            A Better Referral Experience for Your Business and Your Customers
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 xl:gap-7 max-w-5xl xl:max-w-7xl mx-auto">
          {BENEFITS.map((b) => (
            <div key={b.title} className="rounded-2xl border border-gray-200 bg-white p-5 xl:p-7 hover:border-blue-200 hover:shadow-sm transition-all">
              <div className="w-9 h-9 xl:w-12 xl:h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-700 mb-3 xl:mb-4">
                {b.icon}
              </div>
              <h3 className="text-sm lg:text-base xl:text-xl font-semibold text-gray-900 mb-1.5 xl:mb-2.5">{b.title}</h3>
              <p className="text-sm lg:text-base xl:text-lg text-gray-500 leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section: Features ────────────────────────────────────────────────────────

const FEATURE_ROWS = [
  {
    headline: 'Find Out Who Is Available Before You Send the Customer',
    copy: 'When more than one trusted partner may be able to help, send a private availability request. Review the responses, choose the right contractor, and send the referral without posting the job publicly.',
    Visual: AvailabilityIllustration,
  },
  {
    headline: 'Stop Wondering What Happened After the Introduction',
    copy: 'Follow every referral from pending through completion. Keep job details, messages, attachments, and close-out information attached to the same record.',
    Visual: StatusTrackingIllustration,
  },
  {
    headline: 'Keep Referral Conversations With the Referral',
    copy: 'Referral-specific messaging keeps important questions, updates, photos, and job details together instead of spreading them across unrelated text threads.',
    Visual: MessagingIllustration,
  },
  {
    headline: 'Know Which Trades Are Missing Before a Customer Asks',
    copy: 'See the gaps in your partner network and focus on building relationships with contractors who can cover the work your company does not perform.',
    Visual: NetworkGapsIllustration,
  },
  {
    headline: 'Turn In-Person Connections Into Real Partners',
    copy: 'Use QR codes and invitation links to connect with contractors you meet at supply houses, trade events, jobsites, and local networking meetings.',
    Visual: QRConnectionIllustration,
  },
  {
    headline: 'See Which Relationships Are Producing Real Business',
    copy: 'Review referrals sent and received with each partner and understand the activity and revenue generated through your network.',
    Visual: AnalyticsIllustration,
  },
];

function FeaturesSection() {
  return (
    <section id="features" className={`${SECTION_Y} bg-gray-50 scroll-mt-20`}>
      <div className={SHELL}>
        <div className="max-w-xl xl:max-w-3xl mx-auto text-center mb-14 xl:mb-20">
          <h2 className={H2}>
            Everything You Need to Manage Contractor Referrals
          </h2>
        </div>
        <div className="space-y-16 sm:space-y-20 xl:space-y-28">
          {FEATURE_ROWS.map(({ headline, copy, Visual }, i) => {
            const textFirst = i % 2 === 0;
            return (
              <div key={headline} className="grid md:grid-cols-2 gap-10 md:gap-16 xl:gap-24 items-center">
                {/* Text is always first in DOM for correct mobile reading order.
                    On desktop, CSS order flips the columns for visual-on-left rows. */}
                <div className={!textFirst ? 'md:order-last' : ''}>
                  <h3 className={`${H3_FEATURE} mb-4 xl:mb-6`}>{headline}</h3>
                  <p className={LEAD}>{copy}</p>
                </div>
                <div className={!textFirst ? 'md:order-first' : ''}>
                  <Visual />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Section: Existing Network ────────────────────────────────────────────────

function ExistingNetworkSection() {
  return (
    <section className={`${SECTION_Y} bg-white`}>
      <div className={SHELL}>
        <div className="max-w-3xl xl:max-w-5xl mx-auto text-center">
          <h2 className={`${H2} mb-4 xl:mb-6`}>
            Start With the Contractors You Already Trust
          </h2>
          <p className={`${LEAD} mb-6 xl:mb-8`}>
            Trade Loop does not require you to wait for a large public network. Invite the plumbers, electricians, restoration companies, roofers, HVAC contractors, remodelers, and other professionals you already refer to.
          </p>
          <ul className="grid sm:grid-cols-2 gap-3 xl:gap-4 max-w-xl xl:max-w-3xl mx-auto mb-8 xl:mb-10 text-left">
            {[
              'Invite partners by link or QR code',
              'Organize partners by trade and service area',
              'Send referrals directly to the contractor you choose',
              'Build the network around your own standards',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm lg:text-base xl:text-lg text-gray-600">
                <CheckCircle className="w-4 h-4 xl:w-5 xl:h-5 text-blue-600 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
          <Link
            to="/login?mode=signup"
            className="inline-flex items-center gap-2 px-6 py-3 xl:px-8 xl:py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors text-sm xl:text-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            Start Building Your Network
            <ArrowRight className="w-4 h-4 xl:w-5 xl:h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Comparison ──────────────────────────────────────────────────────

function ComparisonSection() {
  const cols = [
    {
      heading: 'Typical Lead Marketplace',
      style: 'gray',
      rows: [
        'Customer opportunities may be distributed broadly',
        'Contractors may compete for the same opportunity',
        'The platform controls access to leads',
        'Focused on acquiring unknown customers',
        'Primarily transactional',
      ],
    },
    {
      heading: 'Trade Loop',
      style: 'blue',
      rows: [
        'You choose the contractor receiving the referral',
        'Referrals are direct partner-to-partner handoffs',
        'You build and control your partner network',
        'Focused on serving existing customers',
        'Built around long-term contractor relationships',
      ],
    },
  ];

  return (
    <section className={`${SECTION_Y} bg-gray-50`}>
      <div className={SHELL}>
        <div className="max-w-xl xl:max-w-3xl mx-auto text-center mb-12 xl:mb-16">
          <h2 className={H2}>
            Contractor Referrals Without the Lead-Marketplace Model
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 xl:gap-6 max-w-3xl xl:max-w-5xl mx-auto">
          {cols.map(({ heading, style, rows }) => (
            <div
              key={heading}
              className={`rounded-2xl overflow-hidden border ${style === 'blue' ? 'border-blue-200' : 'border-gray-200'}`}
            >
              <div className={`px-5 py-3 xl:px-7 xl:py-4 ${style === 'blue' ? 'bg-blue-600' : 'bg-gray-50 border-b border-gray-200'}`}>
                <p className={`text-sm xl:text-lg font-semibold ${style === 'blue' ? 'text-white' : 'text-gray-600'}`}>{heading}</p>
              </div>
              <ul className={`divide-y ${style === 'blue' ? 'bg-blue-50 divide-blue-100' : 'bg-white divide-gray-100'}`}>
                {rows.map((row) => (
                  <li key={row} className="flex items-start gap-3 px-5 py-3.5 xl:px-7 xl:py-4">
                    {style === 'blue' ? (
                      <CheckCircle className="w-4 h-4 xl:w-5 xl:h-5 text-blue-600 shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-4 h-4 xl:w-5 xl:h-5 text-gray-300 shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm lg:text-base xl:text-lg ${style === 'blue' ? 'text-blue-900' : 'text-gray-500'}`}>{row}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-8 xl:mt-10 text-sm lg:text-base xl:text-lg text-gray-500 text-center max-w-2xl xl:max-w-4xl mx-auto">
          Trade Loop does not sell the same customer opportunity to multiple contractors. It helps contractors professionally manage the referrals they are already making.
        </p>
      </div>
    </section>
  );
}

// ─── Section: Built From Trades ───────────────────────────────────────────────

function BuiltFromTradesSection() {
  return (
    <section className={`${SECTION_Y} bg-white`}>
      <div className={SHELL}>
        <div className="max-w-2xl xl:max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 xl:w-16 xl:h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-5 xl:mb-7">
            <Wrench className="w-6 h-6 xl:w-8 xl:h-8 text-blue-700" />
          </div>
          <h2 className={`${H2} mb-4 xl:mb-6`}>
            Built From Inside the Trades
          </h2>
          <p className={`${LEAD} mb-4 xl:mb-6`}>
            Trade Loop was built from firsthand experience inside the residential service trades after seeing valuable customer referrals managed through scattered calls, texts, screenshots, and memory.
          </p>
          <p className={`${LEAD} mb-6 xl:mb-8`}>
            The goal is simple: give contractors a professional system for managing the referral relationships that protect their customers, strengthen their reputation, and create real business value.
          </p>
          {/* Credibility statement */}
          <div className="inline-flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-2xl px-5 py-3.5 xl:px-7 xl:py-5 mb-5 xl:mb-7">
            <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" aria-hidden="true" />
            <p className="text-sm lg:text-base xl:text-lg font-semibold text-blue-800 text-left">
              Built by a contractor who experienced the referral problem firsthand.
            </p>
          </div>
          <p className="text-sm lg:text-base xl:text-lg font-semibold text-blue-700">
            Built from real contractor experience. Designed around real contractor workflows.
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Boise Launch ────────────────────────────────────────────────────

function BoiseLaunchSection() {
  return (
    <section
      className={SECTION_Y}
      style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 55%, #065f46 100%)' }}
    >
      <div className={`${SHELL} text-center`}>
        <div className="flex items-center justify-center gap-2 mb-3">
          <MapPin className="w-4 h-4 xl:w-5 xl:h-5 text-blue-200" aria-hidden="true" />
          <span className="text-xs xl:text-sm font-bold text-blue-200 uppercase tracking-widest">First Launch Market</span>
        </div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white tracking-tight mb-4 xl:mb-6">
          Launching First With Contractors in Boise and the Treasure Valley
        </h2>
        <p className="text-base sm:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-2xl xl:max-w-4xl mx-auto mb-3 xl:mb-4">
          Trade Loop is beginning in Boise, Meridian, Eagle, Garden City, Star, Kuna, Nampa, Caldwell, Middleton, and surrounding Treasure Valley communities before expanding into additional markets.
        </p>
        <p className="text-sm lg:text-base xl:text-lg text-blue-200 mb-8 xl:mb-10">
          Local contractors can begin by inviting the referral partners they already know and trust.
        </p>
        <Link
          to="/login?mode=signup"
          className="inline-flex items-center gap-2 px-6 py-3.5 xl:px-8 xl:py-4 bg-white hover:bg-blue-50 text-blue-700 font-semibold rounded-xl transition-colors text-sm xl:text-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-blue-800"
        >
          Create Free Account
          <ArrowRight className="w-4 h-4 xl:w-5 xl:h-5" />
        </Link>
      </div>
    </section>
  );
}

// ─── Section: Pricing ─────────────────────────────────────────────────────────

function PricingSection({ plans }: { plans: PricingPlan[] }) {
  return (
    <section id="pricing" className={`${SECTION_Y} bg-gray-50 scroll-mt-20`}>
      <div className={SHELL}>
        <div className="max-w-xl xl:max-w-3xl mx-auto text-center mb-12 xl:mb-16">
          <h2 className={H2}>
            Start Free. Upgrade When Your Business Needs More.
          </h2>
          <p className={`mt-3 xl:mt-5 ${LEAD}`}>
            Build your network and manage referrals without paying for every connection or customer handoff.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 xl:gap-8 max-w-5xl xl:max-w-7xl mx-auto items-start">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`rounded-2xl border p-6 xl:p-8 relative ${
                plan.highlighted
                  ? 'bg-blue-600 border-blue-500 shadow-xl ring-2 ring-blue-400 ring-offset-2'
                  : 'bg-white border-gray-200'
              } ${!plan.available ? 'opacity-75' : ''}`}
            >
              {plan.badge && (
                <div
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs xl:text-sm font-bold whitespace-nowrap ${
                    plan.highlighted ? 'bg-white text-blue-700' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {plan.badge}
                </div>
              )}
              <h3 className={`text-lg xl:text-2xl font-bold mb-1 ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                {plan.name}
              </h3>
              <div className="flex items-end gap-0.5 mb-2">
                <span className={`text-3xl xl:text-5xl font-extrabold ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                  {plan.price}
                </span>
                {plan.period && (
                  <span className={`text-sm xl:text-base mb-1 ${plan.highlighted ? 'text-blue-200' : 'text-gray-400'}`}>
                    {plan.period}
                  </span>
                )}
              </div>
              <p className={`text-sm lg:text-base xl:text-lg leading-relaxed mb-5 xl:mb-7 ${plan.highlighted ? 'text-blue-100' : 'text-gray-500'}`}>
                {plan.description}
              </p>
              <ul className="space-y-2.5 xl:space-y-3.5 mb-6 xl:mb-8">
                {plan.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2.5 text-sm lg:text-base xl:text-lg ${plan.highlighted ? 'text-blue-50' : 'text-gray-600'}`}>
                    <CheckCircle className={`w-4 h-4 xl:w-5 xl:h-5 shrink-0 mt-0.5 ${plan.highlighted ? 'text-blue-200' : 'text-blue-600'}`} />
                    {f}
                  </li>
                ))}
              </ul>
              {plan.available ? (
                <Link
                  to={plan.href}
                  className={`block w-full text-center py-3 xl:py-4 rounded-xl font-semibold text-sm xl:text-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                    plan.highlighted
                      ? 'bg-white text-blue-700 hover:bg-blue-50 focus-visible:ring-white focus-visible:ring-offset-blue-600'
                      : 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500'
                  }`}
                >
                  {plan.cta}
                </Link>
              ) : (
                <button
                  disabled
                  aria-disabled="true"
                  className="block w-full text-center py-3 xl:py-4 rounded-xl font-semibold text-sm xl:text-lg bg-gray-100 text-gray-400 cursor-not-allowed"
                >
                  {plan.cta}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section: FAQ ─────────────────────────────────────────────────────────────

function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <section id="faq" className={`${SECTION_Y} bg-white scroll-mt-20`}>
      <div className={SHELL}>
        <div className="max-w-xl xl:max-w-3xl mx-auto text-center mb-12 xl:mb-16">
          <h2 className={H2}>Common Questions</h2>
        </div>
        <div className="max-w-2xl xl:max-w-4xl mx-auto border border-gray-100 rounded-2xl overflow-hidden shadow-sm bg-white divide-y divide-gray-100">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={item.question}>
                <button
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                  id={`faq-btn-${i}`}
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 xl:px-7 xl:py-6 text-left text-sm lg:text-base xl:text-xl font-semibold text-gray-900 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                >
                  <span>{item.question}</span>
                  <ChevronDown
                    className={`w-4 h-4 xl:w-5 xl:h-5 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                <div
                  role="region"
                  id={`faq-panel-${i}`}
                  aria-labelledby={`faq-btn-${i}`}
                  hidden={!isOpen}
                  className="px-5 pb-5 xl:px-7 xl:pb-7 text-sm lg:text-base xl:text-lg text-gray-600 leading-relaxed"
                >
                  {item.answer}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Section: Final CTA ───────────────────────────────────────────────────────

function FinalCTASection() {
  return (
    <section
      className={`${SECTION_Y} relative overflow-hidden`}
      style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #065f46 100%)' }}
    >
      {/* Subtle glow overlays */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute top-0 left-1/4 w-64 h-64 xl:w-96 xl:h-96 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 xl:w-96 xl:h-96 rounded-full bg-emerald-400/10 blur-3xl" />
      </div>
      <div className={`relative ${SHELL} text-center`}>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white tracking-tight mb-4 xl:mb-6">
          Put Your Referral Network to Work
        </h2>
        <p className="text-base sm:text-lg lg:text-xl text-blue-100 max-w-xl xl:max-w-3xl mx-auto mb-8 xl:mb-10 leading-relaxed">
          Start with the contractors you already trust. Send better referrals, follow every customer handoff, and build stronger local business relationships.
        </p>
        <Link
          to="/login?mode=signup"
          className="inline-flex items-center gap-2 px-7 py-4 xl:px-10 xl:py-5 bg-white hover:bg-blue-50 text-blue-800 font-bold rounded-xl transition-colors text-base xl:text-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-blue-900 shadow-lg"
        >
          Create Free Account
          <ArrowRight className="w-4 h-4 xl:w-5 xl:h-5" />
        </Link>
        <p className="mt-4 text-sm xl:text-base text-blue-200/70">
          Free plan available · Unlimited referrals and partner connections
        </p>
      </div>
    </section>
  );
}

// ─── Section: Footer ──────────────────────────────────────────────────────────

function MarketingFooter({ onNav }: { onNav: (id: string) => void }) {
  const year = new Date().getFullYear();

  /* The mockup pointed at /support, /privacy and /terms, none of which are routes in
     this app. Mapped to the pages that actually exist.

     No 'Plans' entry here on purpose. It used to route to the standalone /plans
     screen, which duplicated the Product column's 'Pricing' anchor now that the
     pricing section lives on this page. One link, one destination — and this
     column is for account actions, not product info. /plans itself is untouched
     and still reached from Settings and the in-app upgrade CTAs. */
  const accountLinks = [
    { label: 'Sign In', href: '/login' },
    { label: 'Create Free Account', href: '/login?mode=signup' },
    { label: 'Support', href: '/contact-us' },
    { label: 'Privacy & Terms', href: '/legal' },
  ];

  return (
    <footer className="bg-gray-950 border-t border-gray-800">
      <div className={`${SHELL} py-12 sm:py-16 xl:py-20`}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 xl:gap-12 mb-10 xl:mb-14">
          <div className="lg:col-span-2">
            <img
              src="/logo-with-text.png"
              alt="Trade Loop"
              className="h-10 sm:h-11 lg:h-12 xl:h-14 w-auto mb-3 xl:mb-5 brightness-0 invert"
            />
            <p className="text-sm lg:text-base xl:text-lg text-gray-400 leading-relaxed max-w-xs xl:max-w-md">
              Contractor referral-management software for residential home-service companies. Build trusted partner networks, send and track referrals, and keep customer handoffs organized.
            </p>

            {/* The install channel, now that the PWA is gone (see the note in LandingPage's main
                export). Both official badges carry an #a6a6a6 outline, so they read correctly on
                this near-black footer without a light variant. The heading is gated too — this
                page is reachable from inside the native apps, where the badges render nothing. */}
            {APP_STORE_BADGES_VISIBLE && (
              <>
                <p className="mt-6 xl:mt-8 text-xs xl:text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 xl:mb-4">
                  Get the mobile app
                </p>
                <AppStoreBadges className="-ml-1" />
              </>
            )}
          </div>

          <div>
            <p className="text-xs xl:text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 xl:mb-4">Product</p>
            <ul className="space-y-2 xl:space-y-3">
              {NAV_ANCHORS.map(({ label, id }) => (
                <li key={id}>
                  <button
                    onClick={() => onNav(id)}
                    className="text-sm lg:text-base xl:text-lg text-gray-400 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs xl:text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 xl:mb-4">Account</p>
            <ul className="space-y-2 xl:space-y-3">
              {accountLinks.map(({ label, href }) => (
                <li key={label}>
                  <Link to={href} className="text-sm lg:text-base xl:text-lg text-gray-400 hover:text-white transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-6 xl:pt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs xl:text-sm text-gray-600">© {year} Trade Loop Holdings, Inc.</p>
          <p className="text-xs xl:text-sm text-gray-600">Boise, Idaho · Referral Management for Residential Contractors</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Structured Data ─────────────────────────────────────────────────────────

function StructuredData() {
  const json = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: 'Trade Loop Holdings, Inc.',
        url: 'https://gotradeloop.com',
        description:
          'Contractor referral-management software for residential home-service companies.',
      },
      {
        '@type': 'SoftwareApplication',
        name: 'Trade Loop',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: 'https://gotradeloop.com',
        description:
          'Build a trusted contractor referral network, send and track customer referrals, request partner availability, and manage every handoff.',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: 'Free Starter plan with unlimited referrals and partner connections.',
        },
      },
    ],
  });
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />;
}

// ─── Floating Contractor Review Card ──────────────────────────────────────────

const STAR_PATH =
  'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';

type CTBStatus = 'hidden' | 'expanded' | 'minimized';

function FloatingContractorReviews() {
  const [isMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  const [reducedMotion] = useState<boolean>(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false,
  );

  // Visibility state.
  // `hasAutoMinimized` starts true on phones because the card reveals straight into the
  // collapsed pill there — the auto-minimize timer has effectively already had its turn, and
  // without this a tap to expand would be yanked closed again 10s later mid-read.
  const [status, setStatus] = useState<CTBStatus>('hidden');
  const [hasAutoMinimized, setHasAutoMinimized] = useState(isMobile);

  // Carousel state
  const [current, setCurrent] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [slideDir, setSlideDir] = useState<'right' | 'left'>('right');
  const [isPaused, setIsPaused] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [timerKey, setTimerKey] = useState(0);
  const [liveRegion, setLiveRegion] = useState('');

  // Reveal after 1 second — expanded on desktop, but only as the collapsed pill on phones,
  // where the expanded card covered ~43% of a 667px-tall screen on arrival. Tapping the pill
  // opens it, and it then stays open.
  useEffect(() => {
    const id = setTimeout(() => setStatus(isMobile ? 'minimized' : 'expanded'), 1000);
    return () => clearTimeout(id);
  }, [isMobile]); // isMobile is captured once at mount, so this still runs a single time

  // Auto-minimize: 12s desktop / 10s mobile; once per session
  const autoMinimizeMs = isMobile ? 10000 : 12000;
  useEffect(() => {
    if (
      status !== 'expanded' ||
      hasAutoMinimized ||
      isPaused ||
      isHovering ||
      isFocused ||
      reducedMotion
    )
      return;
    const id = setTimeout(() => {
      setStatus('minimized');
      setHasAutoMinimized(true);
    }, autoMinimizeMs);
    return () => clearTimeout(id);
  }, [
    status,
    hasAutoMinimized,
    isPaused,
    isHovering,
    isFocused,
    reducedMotion,
    autoMinimizeMs,
  ]);

  // Escape key → minimize expanded card
  useEffect(() => {
    if (status !== 'expanded') return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setStatus('minimized');
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [status]);

  // Carousel auto-advance — paused when minimized or hidden
  const shouldAutoplay =
    status === 'expanded' &&
    !isPaused &&
    !isHovering &&
    !isFocused &&
    !reducedMotion;

  useEffect(() => {
    if (!shouldAutoplay) return;
    const id = setInterval(() => {
      setSlideDir('right');
      setCurrent((c) => (c + 1) % TOTAL_TESTIMONIALS);
      setAnimKey((k) => k + 1);
    }, 8000);
    return () => clearInterval(id);
  }, [shouldAutoplay, timerKey]);

  const goTo = (idx: number, dir: 'right' | 'left', manual: boolean) => {
    setSlideDir(dir);
    setCurrent(idx);
    setAnimKey((k) => k + 1);
    setTimerKey((k) => k + 1);
    if (manual) {
      const entry = CONTRACTOR_TESTIMONIALS[idx];
      setLiveRegion(
        `Review ${idx + 1} of ${TOTAL_TESTIMONIALS}: ${entry.name}, ${entry.company}`,
      );
      setTimeout(() => setLiveRegion(''), 1200);
    }
  };

  const handleNext = () => goTo((current + 1) % TOTAL_TESTIMONIALS, 'right', true);
  const handlePrev = () => goTo((current - 1 + TOTAL_TESTIMONIALS) % TOTAL_TESTIMONIALS, 'left', true);
  const handleTogglePause = () => setIsPaused((v) => !v);
  const handleMinimize = () => setStatus('minimized');
  const handleExpand = () => setStatus('expanded');

  // Touch / swipe — horizontal only; don't block vertical scrolling
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const trackingSwipe = useRef(false);

  const onTouchStart = (e: ReactTouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    trackingSwipe.current = false;
  };
  const onTouchMove = (e: ReactTouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = Math.abs(e.touches[0].clientX - touchStartX.current);
    const dy = Math.abs(e.touches[0].clientY - touchStartY.current);
    if (dx > dy && dx > 10) trackingSwipe.current = true;
    else if (dy > dx && dy > 10) {
      touchStartX.current = null;
      trackingSwipe.current = false;
    }
  };
  const onTouchEnd = (e: ReactTouchEvent) => {
    if (!trackingSwipe.current || touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 40) {
      if (diff < 0) handleNext();
      else handlePrev();
    }
    touchStartX.current = null;
    trackingSwipe.current = false;
  };

  const enterAnimClass = !reducedMotion
    ? slideDir === 'right'
      ? 'tl-testimonial-enter-right'
      : 'tl-testimonial-enter-left'
    : '';
  const floatInClass = !reducedMotion ? 'tl-float-in' : '';

  const t = CONTRACTOR_TESTIMONIALS[current];

  // ── Shared sub-components ────────────────────────────────────────────────

  const Stars = ({ small }: { small?: boolean }) => (
    <div
      className="flex items-center gap-0.5"
      role="img"
      aria-label="5 out of 5 stars"
    >
      {[...Array(5)].map((_, i) => (
        <svg
          key={i}
          className={`${small ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-amber-400 fill-current`}
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path d={STAR_PATH} />
        </svg>
      ))}
    </div>
  );

  const QuoteContent = ({ testimonial }: { testimonial: Testimonial }) => (
    <>
      <p className="text-sm text-gray-700 leading-snug">{testimonial.quote}</p>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
        <span className="font-semibold text-gray-800 text-sm">
          {testimonial.name}
        </span>
        <span className="text-gray-300 text-[11px]" aria-hidden="true">
          ·
        </span>
        <span className="text-gray-500 text-xs">{testimonial.company}</span>
        {testimonial.location && (
          <>
            <span className="text-gray-300 text-[11px]" aria-hidden="true">
              ·
            </span>
            <span className="text-gray-500 text-xs">{testimonial.location}</span>
          </>
        )}
      </div>
    </>
  );

  // ── Nothing to render ────────────────────────────────────────────────────
  if (status === 'hidden') return null;

  // ── Shared positioning wrapper classes ───────────────────────────────────
  // Desktop: bottom-right card. Mobile: full-width bottom sheet.
  const positionClasses =
    'fixed z-40 bottom-0 left-3 right-3 sm:bottom-6 sm:right-6 sm:left-auto sm:w-[400px] xl:w-[440px] sm:max-w-[calc(100vw-48px)]';

  // ── Collapsed pill ───────────────────────────────────────────────────────
  if (status === 'minimized') {
    return (
      <>
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {liveRegion}
        </div>
        <div
          className={`${positionClasses} ${floatInClass}`}
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div
            className="flex items-center bg-white rounded-t-2xl sm:rounded-full border border-gray-200 overflow-hidden"
            style={{
              boxShadow:
                '0 4px 20px rgba(0,0,0,0.10), 0 0 0 1px rgba(37,99,235,0.08)',
            }}
          >
            <button
              onClick={handleExpand}
              aria-label="Open contractor reviews"
              className="flex items-center gap-2 pl-4 pr-3 py-3.5 sm:py-3 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset flex-1 min-w-0"
            >
              <Stars small />
              <span className="text-sm font-medium text-gray-700 truncate">
                Contractor feedback
              </span>
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Expanded card ────────────────────────────────────────────────────────
  return (
    <>
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {liveRegion}
      </div>
      <div
        role="region"
        aria-label="Feedback from local contractors"
        className={`${positionClasses} ${floatInClass}`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node))
            setIsFocused(false);
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="relative bg-white border border-gray-100 overflow-hidden rounded-t-2xl rounded-b-none sm:rounded-2xl"
          style={{
            boxShadow:
              '0 4px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(37,99,235,0.07)',
          }}
        >
          {/* Blue-to-green top accent line */}
          <div
            className="absolute inset-x-0 top-0 h-0.5"
            style={{
              background: 'linear-gradient(90deg, #2563eb 0%, #059669 100%)',
            }}
            aria-hidden="true"
          />

          <div className="px-4 pt-4 pb-3.5 sm:px-5">
            {/* Header: badge + minimize */}
            <div className="flex items-center gap-2 mb-2.5">
              <div
                className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-600 text-[10px] font-semibold uppercase tracking-wide rounded-full px-2.5 py-1 leading-none flex-1 min-w-0"
                aria-hidden="true"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"
                  aria-hidden="true"
                />
                <span className="truncate">
                  Feedback from local contractors
                </span>
              </div>
              <button
                onClick={handleMinimize}
                aria-label="Minimize contractor reviews"
                className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Stars */}
            <div className="mb-2.5">
              <Stars />
            </div>

            {/* Quote + attribution — CSS grid stack for stable card height */}
            <div style={{ display: 'grid' }}>
              {CONTRACTOR_TESTIMONIALS.map((anchor, i) => (
                <div
                  key={`anchor-${i}`}
                  style={{
                    gridArea: '1 / 1',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                  }}
                  aria-hidden="true"
                >
                  <QuoteContent testimonial={anchor} />
                </div>
              ))}
              <div
                key={animKey}
                style={{ gridArea: '1 / 1' }}
                className={enterAnimClass}
              >
                <QuoteContent testimonial={t} />
              </div>
            </div>

            {/* Controls */}
            <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-between">
              <span
                className="text-[11px] font-medium text-gray-400 tabular-nums select-none"
                aria-label={`Review ${current + 1} of ${TOTAL_TESTIMONIALS}`}
              >
                {current + 1} / {TOTAL_TESTIMONIALS}
              </span>
              <div className="flex items-center gap-0.5">
                <button
                  onClick={handlePrev}
                  aria-label="Previous contractor review"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={handleNext}
                  aria-label="Next contractor review"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={handleTogglePause}
                  aria-label={
                    isPaused
                      ? 'Resume contractor reviews'
                      : 'Pause contractor reviews'
                  }
                  aria-pressed={isPaused}
                  className={`p-1.5 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
                    isPaused
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  {isPaused ? (
                    <Play className="w-3.5 h-3.5" />
                  ) : (
                    <Pause className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function LandingPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const { plans } = usePricing();
  // Redirect authenticated users to dashboard
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  /* The PWA install prompts that used to live here are gone. Trade Loop is supported in the
     browser and in the native iOS/Android apps — there is no third, worse channel to install.
     Card 27 had already had to add an `isNative` guard because the iOS banner rendered inside
     the installed native app (the UA still says iPhone, and `navigator.standalone` is
     Safari-only, so the standalone check was false there). Card 30 removes the banners
     outright, along with the manifest that made the browser offer "Install app" at all. */

  const nav = (id: string) => scrollToId(id);
  const pricingPlans = buildPricingPlans(
    plans?.pro?.formatted.split('/')[0] || '$29',
    plans?.grow?.formatted.split('/')[0] || '$79',
  );

  return (
    <>
      <StructuredData />
      <div className="min-h-screen bg-white text-gray-900 antialiased overflow-x-hidden">
        <MarketingHeader onNav={nav} />
        <main id="main-content">
          <HeroSection onHowItWorks={() => nav('how-it-works')} />
          <DifferentiationStrip />
          <ProblemSection />
          <HowItWorksSection />
          <BenefitsSection />
          <FeaturesSection />
          <ExistingNetworkSection />
          <ComparisonSection />
          <BuiltFromTradesSection />
          <BoiseLaunchSection />
          <PricingSection plans={pricingPlans} />
          <FAQSection />
          <FinalCTASection />
        </main>
        <MarketingFooter onNav={nav} />
      </div>
      <FloatingContractorReviews />
    </>
  );
}
